// Pure URL <-> Route serialization for the hash router. No DOM / state imports,
// so it's unit-testable under Node and reusable by router.ts. The grammar:
//
//   #/highlights                    the landing digest (empty hash also lands here)
//   #/dashboard/<metric>            e.g. #/dashboard/cost_artifact
//   #/artifacts/<kind>[?q=&sort=&dir=]   kind = feature | pr | ticket
//   #/sessions[?win=&q=&outcomes=&artifact=&artifactKind=&team=&f.<facet>=…]
//   ...any of the above + ?session=<id>   (the open detail drawer, orthogonal)
//
// The path picks the screen; the query string carries that screen's filtered-list
// state, so a filtered list (and a future graph drill-down into one) is a plain
// URL. Facet filters are namespaced `f.<key>` so a dynamic facet name can't
// collide with a reserved param. Unknown views/metrics/kinds fall back to
// defaults rather than erroring, so a stale or hand-mangled hash always resolves.

export interface Route {
  view: 'dashboard' | 'artifacts' | 'sessions' | 'settings' | 'benchmarks' | 'taskreplay' | 'replay' | 'skills' | 'tools' | 'insights' | 'models'
  metric: string // dashboard sub-selection (which KPI is expanded)
  artKind: string // artifacts sub-selection (feature | pr | ticket)
  insightsSub: 'highlights' | 'recommendations' // insights subtab
  session: string | null // open drawer target, or null (mirror of query.session)
  bug: string | null // open defect drawer's ticket key (mirror of query.bug)
  query: Record<string, string> // full decoded query string (filtered-list state)
}

/** The path-level slice of client state that maps to the URL path. */
export interface NavState {
  view: 'dashboard' | 'artifacts' | 'sessions' | 'settings' | 'benchmarks' | 'taskreplay' | 'replay' | 'skills' | 'tools' | 'insights' | 'models'
  metric: string | null
  artKind: string
  insightsSub: 'highlights' | 'recommendations'
}

// VIEWS is a membership list and its order does not affect the nav, but it
// matches the tab bar (Insights, Metrics, Skills, MCP/Tools, Models, Artifacts,
// Sessions, Benchmarks, Settings) so the two cannot drift into disagreeing about it.
// The 'dashboard' view id stays internal — its tab reads "Metrics" but every
// DOM id, route slug, and setView('dashboard') keeps the id.
// 'highlights' is accepted as a legacy alias for 'insights' (backward compat).
export const VIEWS = ['dashboard', 'skills', 'insights', 'tools', 'models', 'artifacts', 'sessions', 'benchmarks', 'taskreplay', 'replay', 'settings']
export const INSIGHTS_SUBS = ['highlights', 'recommendations']
// Routable metric detail sections — one per headline tile, in row order.
// 'ops' is absent: the tool-error tile was dropped from the headline row, so a
// hash pointing at it is a dead end and falls back to the default metric.
export const METRICS = ['success_rate', 'cost_artifact', 'defect_rate', 'churn', 'time_to_ship', 'total_spend']
export const ART_KINDS = ['feature', 'pr', 'ticket']
export const DEFAULT_METRIC = 'cost_artifact'
export const DEFAULT_ARTKIND = 'feature'

/** Decode a `a=1&b=2` query string into a map (tolerant of junk / bad encoding). */
export function parseQuery(str: string): Record<string, string> {
  const out: Record<string, string> = {}
  ;(str || '').split('&').forEach((kv) => {
    if (!kv) return
    const eq = kv.indexOf('=')
    const k = eq < 0 ? kv : kv.slice(0, eq)
    const v = eq < 0 ? '' : kv.slice(eq + 1)
    if (!k) return
    try {
      out[decodeURIComponent(k)] = decodeURIComponent(v)
    } catch {
      /* drop a malformed pair rather than throw */
    }
  })
  return out
}

/** Encode a map into a stable (key-sorted) query string; empty values dropped. */
export function serializeQuery(query: Record<string, string>): string {
  return Object.keys(query)
    .filter((k) => query[k] != null && query[k] !== '')
    .sort()
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(query[k]))
    .join('&')
}

/** Parse a location.hash string into a fully-defaulted Route (never throws). */
export function parseHash(hash: string): Route {
  // '#/dashboard/cost_artifact?session=abc' → path 'dashboard/cost_artifact', query 'session=abc'
  const raw = (hash || '').replace(/^#\/?/, '')
  const qIdx = raw.indexOf('?')
  const path = qIdx >= 0 ? raw.slice(0, qIdx) : raw
  const query = parseQuery(qIdx >= 0 ? raw.slice(qIdx + 1) : '')
  const parts = path.split('/').filter(Boolean)

  // 'highlights' is a legacy alias: map it to insights view, highlights subtab.
  const rawView = parts[0] === 'highlights' ? 'insights' : parts[0]
  const view = (VIEWS.indexOf(rawView) >= 0 ? rawView : 'dashboard') as Route['view']
  const metric = view === 'dashboard' && METRICS.indexOf(parts[1]) >= 0 ? parts[1] : DEFAULT_METRIC
  const artKind = view === 'artifacts' && ART_KINDS.indexOf(parts[1]) >= 0 ? parts[1] : DEFAULT_ARTKIND
  // For the insights view, the second path segment selects the subtab.
  // #/highlights (legacy) → highlights subtab; #/insights → highlights (default);
  // #/insights/recommendations → recommendations subtab.
  const subRaw = parts[0] === 'highlights' ? 'highlights' : (view === 'insights' ? parts[1] : undefined)
  const insightsSub = (INSIGHTS_SUBS.indexOf(subRaw as string) >= 0 ? subRaw : 'highlights') as Route['insightsSub']

  return { view, metric, artKind, insightsSub, session: query.session || null, bug: query.bug || null, query }
}

/** Serialize a path slice + a query map into a canonical hash string. */
export function serializeRoute(nav: NavState, query: Record<string, string>): string {
  const base =
    nav.view === 'insights'
      ? '#/insights' + (nav.insightsSub === 'recommendations' ? '/recommendations' : '')
      : nav.view === 'artifacts'
        ? '#/artifacts/' + (nav.artKind || DEFAULT_ARTKIND)
        : nav.view === 'sessions'
          ? '#/sessions'
          : nav.view === 'settings'
            ? '#/settings'
            : nav.view === 'benchmarks'
              ? '#/benchmarks'
              : nav.view === 'taskreplay'
                ? '#/taskreplay'
                : nav.view === 'replay'
                ? '#/replay'
                : nav.view === 'skills'
                ? '#/skills'
                : nav.view === 'tools'
                  ? '#/tools'
                  : nav.view === 'models'
                    ? '#/models'
                    : '#/dashboard/' + (nav.metric || DEFAULT_METRIC)
  const qs = serializeQuery(query)
  return qs ? base + '?' + qs : base
}
